import React, { ReactNode } from 'react';

import {
  useRecordContext,
} from 'react-admin';

import { isNil, get, isArray } from 'lodash';

import { Chip, Typography } from '@mui/material';

import entityTypes, { EntityTypes } from '../constants/entityTypes';
import { alpha2ToFlagEmoji } from './CountryFlag';
import { EntityContext } from './ReferenceEntityField';
import { IBusiness } from '../utilities/schemas/business';

const INDIVIDUAL_PRIORITY = ['nationalIdentifier', 'firstName+lastName', 'customIds[0]', 'id'] as const;
const BUSINESS_PRIORITY = ['name', 'registrationNumber', 'customIds[0]', 'id'] as const;
const CAR_PRIORITY = ['registrationNumber', 'vinCode', 'carModel', 'customIds[0]', 'id'] as const;
const ACCOUNT_PRIORITY = ['accountNumber', 'accountName', 'customIds[0]', 'id'] as const;
const PRODUCT_PRIORITY = ['productType+productInformation.phoneNumber', 'productType+productNumber', 'customIds[0]', 'id'] as const;
const UNKNOWN_PRIORITY = ['name', 'firstName+lastName'] as const;

const PRIORITY = {
  [entityTypes.INDIVIDUAL]: INDIVIDUAL_PRIORITY,
  [entityTypes.BUSINESS]: BUSINESS_PRIORITY,
  [entityTypes.CAR]: CAR_PRIORITY,
  [entityTypes.ACCOUNT]: ACCOUNT_PRIORITY,
  [entityTypes.PRODUCT]: PRODUCT_PRIORITY,
  [entityTypes.UNKNOWN]: UNKNOWN_PRIORITY,
};

const isValidField = (value: unknown) => !isNil(value) && value !== 'Unknown' && (!isArray(value) || value.length > 0);

const ChipOrText = ({
  children,
  chip,
  hasLink,
}: {
  children: ReactNode;
  chip: boolean;
  hasLink: boolean;
  // Used for react admin to put in table headers
  label?: string;
}) => {
  if (chip) {
    return (
      <Chip
        label={children}
        color="neutral"
        sx={hasLink ? { '& .MuiChip-label': { textDecoration: 'underline' } } : {}}
        clickable={hasLink}
      />
    );
  }
  return <Typography sx={hasLink ? { textDecoration: 'underline' } : {}}>{children}</Typography>;
};

export const getEntityDisplayName = ({
  record,
  type,
}: {
  record: Record<string, unknown>;
  type: EntityTypes;
}) => {
  let output = 'Unknown';
  let fieldName = 'Unknown';

  PRIORITY[type].some((field) => {
    if (field.includes('+')) {
      const [field1, field2] = field.split('+');

      if (isValidField(get(record, field1)) && isValidField(get(record, field2))) {
        const combinedValue = `${get(record, field1)} ${get(record, field2)}`.trim();
        output = combinedValue;
        fieldName = field;
        return true;
      }
    }

    if (isValidField(get(record, field))) {
      output = get(record, field) as string;
      fieldName = field;
      return true;
    }

    return false;
  });

  if (fieldName === 'registrationNumber' && output !== 'Unknown' && type === entityTypes.BUSINESS) {
    const { residence } = record as IBusiness;
    return residence ? `${alpha2ToFlagEmoji(residence)} ${output}` : output;
  }

  if (Array.isArray(output) && output.length > 0 && fieldName === 'nationalIdentifier') {
    const text = (output as Array<{country: string; identifier: string}>).slice(0, 2)
      .map((item, index, array) => `${alpha2ToFlagEmoji(item.country)} ${item.identifier}${index < array.length - 1 ? ', ' : ''}`);
    return `${text.join(' ')}${output.length > 2 ? '...' : ''}`;
  }

  return output;
};

const EntityDisplayNameField = ({
  chip = false,
  useEntityContext = true,
  label,
}: {
  chip?: boolean;
  useEntityContext?: boolean;
  label?: string;
}) => {
  const record = useRecordContext();
  const entityContextValue = React.useContext(EntityContext);

  if (!entityContextValue && useEntityContext) return null;
  if (!record) return <Typography>Unknown</Typography>;

  let displayName;
  let type;
  let hasLink;

  if (useEntityContext) {
    type = entityContextValue!.type;
    hasLink = entityContextValue!.hasLink;
    displayName = getEntityDisplayName({ record, type });
  } else {
    hasLink = false;
    displayName = getEntityDisplayName({ record, type: record.type as EntityTypes });
  }

  return <ChipOrText chip={chip} hasLink={hasLink} label={label}>{displayName}</ChipOrText>;
};

export default EntityDisplayNameField;
